import { useCmaAssetClassHistoricalStatsRetrieveQuery } from "@app/api/carApi.generated";
import {
  AssetClassLoadType,
  useAssetClasses,
} from "@app/features/assets/useAssetClasses";
import { useAssetClassSelector } from "@app/hooks/useAssetClassSelector";
import { ExcelDataFormat, useExcelExport } from "@app/hooks/useExcelExport";
import { formatDate } from "@app/utils";
import { useMemo, useState } from "react";

export interface SimulationModelsHistoryItem {
  date: string;
  staticMu: number;
  staticSigma: number;
  dynamicMu: number;
  dynamicSigma: number;
}

export const useSimulationModelsHistory = () => {
  const [hiddenFields, setHiddenFields] = useState<string[]>([]);
  const excelExport = useExcelExport();

  const assetClasses = useAssetClasses({
    type: AssetClassLoadType.allFirm,
  });

  const assetClassSelector = useAssetClassSelector({
    assetClasses,
  });

  const historicalStat = useCmaAssetClassHistoricalStatsRetrieveQuery(
    {
      assetClassId: assetClassSelector.value ?? "",
    },
    {
      skip: !assetClassSelector.value,
    },
  );

  return useMemo(() => {
    const dynData = historicalStat.data?.dynamic;
    const items =
      historicalStat.data?.static
        .map<SimulationModelsHistoryItem>((s) => {
          const d = dynData?.find((d) => d.date === s.date);
          return {
            date: s.date,
            staticMu: s.mu,
            staticSigma: s.sigma,
            dynamicMu: d?.mu ?? 0,
            dynamicSigma: d?.sigma ?? 0,
          };
        })
        .sort((a, b) => a.date.localeCompare(b.date)) ?? [];

    const handleDownloadReports = () => {
      const hasMu = !hiddenFields.includes("mu");
      const hasSigma = !hiddenFields.includes("sigma");
      const filePrefix = `Simulation Models History - ${assetClassSelector.selectedItem?.name}`;
      const bookType = "xlsx";

      if (hasMu && hasSigma) {
        excelExport.exportTable({
          filePrefix,
          bookType,
          table: {
            name: assetClassSelector.selectedItem?.name ?? "",
            bands: [
              { label: "", span: 1 },
              { label: "Static", span: 2 },
              { label: "", span: 0 },
              { label: "Dynamic", span: 2 },
              { label: "", span: 0 },
            ],
            columns: [
              {
                label: "",
                format: ExcelDataFormat.general,
              },
              {
                label: "Mu",
                format: ExcelDataFormat.percent,
              },
              {
                label: "Sigma",
                format: ExcelDataFormat.percent,
              },
              {
                label: "Mu",
                format: ExcelDataFormat.percent,
              },
              {
                label: "Sigma",
                format: ExcelDataFormat.percent,
              },
            ],
            rows: items.map((i) => [
              formatDate(i.date),
              i.staticMu,
              i.staticSigma,
              i.dynamicMu,
              i.dynamicSigma,
            ]),
          },
        });
      } else if (hasMu) {
        excelExport.exportTable({
          filePrefix,
          bookType,
          table: {
            name: assetClassSelector.selectedItem?.name ?? "",
            columns: [
              {
                label: "",
                format: ExcelDataFormat.general,
              },
              {
                label: "Static Mu",
                format: ExcelDataFormat.percent,
              },
              {
                label: "Dynamic Mu",
                format: ExcelDataFormat.percent,
              },
            ],
            rows: items.map((i) => [
              formatDate(i.date),
              i.staticMu,
              i.dynamicMu,
            ]),
          },
        });
      } else if (hasSigma) {
        excelExport.exportTable({
          filePrefix,
          bookType,
          table: {
            name: assetClassSelector.selectedItem?.name ?? "",
            columns: [
              {
                label: "",
                format: ExcelDataFormat.general,
              },
              {
                label: "Static Sigma",
                format: ExcelDataFormat.percent,
              },
              {
                label: "Dynamic Sigma",
                format: ExcelDataFormat.percent,
              },
            ],
            rows: items.map((i) => [
              formatDate(i.date),
              i.staticSigma,
              i.dynamicSigma,
            ]),
          },
        });
      }
    };
    return {
      isLoading: historicalStat.isLoading,
      assetClassSelector,
      items,
      hiddenFields,
      setHiddenFields,
      handleDownloadReports,
    };
  }, [
    assetClassSelector,
    excelExport,
    hiddenFields,
    historicalStat.data?.dynamic,
    historicalStat.data?.static,
    historicalStat.isLoading,
  ]);
};
